import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, concatMap, debounceTime, map } from 'rxjs/operators';
import { ESaveStatus } from '@shared/enums/save-status.enum';
import { IConfiguratorDraft } from '../models/configurator.model';
import { CONFIGURATOR_PERSISTENCE_ADAPTER } from './configurator-persistence.adapter';

const SAVE_DEBOUNCE_MS = 500;

/** Autosave: collects draft changes, debounces them and writes them through the configured adapter. */
@Injectable()
export class ConfiguratorPersistenceService implements OnDestroy {
  private readonly adapter = inject(CONFIGURATOR_PERSISTENCE_ADAPTER);
  private readonly changes$ = new Subject<IConfiguratorDraft>();
  private readonly subscription: Subscription;

  private contextKey: string | null = null;
  private pendingDraft: IConfiguratorDraft | null = null;
  private destroyed = false;

  readonly status = signal<ESaveStatus>(ESaveStatus.IDLE);

  constructor() {
    this.subscription = this.changes$
      .pipe(
        debounceTime(SAVE_DEBOUNCE_MS),
        concatMap(() => this.savePending())
      )
      .subscribe((status) => this.status.set(status));
  }

  /** Nothing typed may be lost when leaving the page, so pending changes are written immediately. */
  ngOnDestroy(): void {
    this.destroyed = true;
    this.subscription.unsubscribe();
    this.flush();
  }

  load(contextKey: string): Observable<IConfiguratorDraft | null> {
    this.contextKey = contextKey;
    return this.adapter.load(contextKey).pipe(catchError(() => of(null)));
  }

  schedule(draft: IConfiguratorDraft): void {
    this.pendingDraft = draft;
    if (this.destroyed) {
      this.flush();
      return;
    }

    this.status.set(ESaveStatus.SAVING);
    this.changes$.next(draft);
  }

  /** Writes a pending draft right away, e.g. when the view is destroyed. */
  flush(): void {
    this.savePending().subscribe((status) => this.status.set(status));
  }

  private savePending(): Observable<ESaveStatus> {
    const draft = this.pendingDraft;
    if (!draft || !this.contextKey) return of(this.status());

    this.pendingDraft = null;
    return this.adapter.save(this.contextKey, draft).pipe(
      map(() => (this.pendingDraft ? ESaveStatus.SAVING : ESaveStatus.SAVED)),
      catchError(() => {
        this.pendingDraft = this.pendingDraft || draft;
        return of(ESaveStatus.ERROR);
      })
    );
  }
}
